import { setNestedObjectValues, useFormik } from "formik";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLeadStore, usePipedriveStore, usePopupStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useLeadDelete from "../../hooks/useLeadDelete";
import useLeadNoteCreate from "../../hooks/useLeadNoteCreate";
import useLeadUpdate from "../../hooks/useLeadUpdate";
import useValidateLeadUpdate from "../../hooks/useValidateLeadUpdate";
import DetailsType from "../../interfaces/DetailsType";
import { ILeadEditState, LeadEditState, LeadStatus } from "../../interfaces/ILead";
import { ClosableOrder } from "../../store/ClosableStore";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanelWithFlyTo from "../DetailsPanelWithFlyTo";
import EditControls, { DeleteControl } from "../EditControls";
import LeadIcon from "../icons/LeadIcon";
import OnMapSpinner from "../spinners/OnMapSpinner";
import LeadFields from "./LeadFields";

function LeadEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const updateLead = useLeadUpdate();
    const deleteLead = useLeadDelete();
    const createLeadNote = useLeadNoteCreate();
    const validateLead = useValidateLeadUpdate();
    const leadStore = useLeadStore();
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const lead = useMemo(() => computed(() => leadStore.leads.find(l => l.id.toString() === id)), [id]).get();
    const activity = useMemo(() => computed(() => pipedriveStore.activities.find(a => a.id === lead?.activityId)), [lead]).get();
    const pipedriveOrganization = useMemo(() => computed(() => lead && pipedriveStore.organizations.find(o => o.id === lead.pipedriveCompanyId)), [lead]).get();

    const close = () => navigate("/");
    const cancel = () => navigate(`/leads/${lead!.id}`);
    useClosable(ClosableOrder.Medium, cancel);

    const remove = async () => {
        await deleteLead(lead!);
        close();
    }

    useEffect(() => {
        if (lead) {
            leadStore.setEditedLeadId(lead.id);
            popupStore.openLeadPopup(lead.id);
        }

        return () => {
            leadStore.setEditedLeadId(null);
            popupStore.close();
        };
    }, [lead]);

    const formik = useFormik<ILeadEditState>({
        initialValues: LeadEditState.empty(),
        validate: validateLead,
        onSubmit: async (values) => {
            await updateLead(lead!, values);
            cancel();
        },
    });

    const changeStatus = async (status: LeadStatus, note: string) => {
        const values = { ...formik.values, status };
        const errors = await formik.validateForm(values);
        if (Object.keys(errors).length > 0) {
            formik.setTouched(setNestedObjectValues(errors, true));
            return;
        }

        await updateLead(lead!, values);
        await createLeadNote(lead!.id, note);
        cancel();
    };

    useEffect(() => {
        if (lead && !leadStore.isLeadsLoading.updating) {
            formik.resetForm({
                values: {
                    status: lead.status,
                    labelId: lead.labelId,
                    address: lead.address,
                    zoning: lead.zoning,
                    landSize: lead.landSize,
                    usableArea: lead.usableArea,
                    buildingSize: lead.buildingSize,
                    lotCoverage: lead.lotCoverage,
                    totalAssessment: lead.totalAssessment,
                    ownerName: lead.ownerName,
                    ownershipType: lead.ownershipType,
                    ownerAddress: lead.ownerAddress,
                    saleDate: lead.saleDate,
                    salePrice: lead.salePrice,
                    pipedriveCompanyName: pipedriveOrganization?.name || null,
                    contactName: lead.contactName,
                    email: lead.email,
                    phoneNumber: lead.phoneNumber,
                    isSetReminder: !!lead.activityId && !!activity,
                    activityId: activity?.id || null,
                    activityDueDate: activity?.dueDate || null,
                    activitySubject: activity?.subject || null,
                },
            });
        }
    }, [lead, activity, pipedriveOrganization]);

    if (leadStore.isLeadsLoading.fetching || pipedriveStore.isActivitiesLoading.fetching)
        return <OnMapSpinner />;

    if (!lead)
        return null;

    return (
        <DetailsPanelWithFlyTo type={DetailsType.LeadEdit} coordinates={lead.centerPoint} onClose={close}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <LeadIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <div>{lead.address}</div>
                        <span className={commonStyles.primary}>{lead.city} {lead.state}</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <LeadFields formik={formik} onChangeStatus={changeStatus} />
            </div>
            <div className={commonStyles.separator}></div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}>
                    <EditControls
                        isStateChanged={formik.dirty}
                        isUpdateLoading={leadStore.isLeadsLoading.updating! || pipedriveStore.isOrganizationsLoading.creating! || pipedriveStore.isActivitiesLoading.creating! || pipedriveStore.isActivitiesLoading.updating! || pipedriveStore.isActivitiesLoading.deleting!}
                        onUpdate={formik.handleSubmit}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}>
                    <DeleteControl
                        entityName="Lead"
                        isLoading={leadStore.isLeadsLoading.deleting!}
                        onDelete={remove}
                    />
                </div>
            </div>
        </DetailsPanelWithFlyTo>
    );
}

export default observer(LeadEdit);
